/**
 * DealerStorefront
 *
 * Public page at /dealer/:id.  Shows a hero block (dealer name, badges,
 * joined date, sold-count "reputation") and a grid of their ACTIVE
 * listings.  Backed by GET /api/dealers/{id}.
 */
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Boxes, Calendar, Share2, Store } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import type { Product, SellerReview, UserPublic } from "../../lib/types";
import { Avatar } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { ProductCard } from "../../components/marketplace/ProductCard";
import { ShareStorefrontModal } from "../../components/ui/ShareStorefrontModal";
import { StarRating } from "../../components/reviews/StarRating";

interface StorefrontPayload {
  dealer: UserPublic;
  listings: Product[];
  stats: {
    active_listings: number;
    sold_count: number;
    joined_at: string;
  };
}

export default function DealerStorefront() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = React.useState<StorefrontPayload | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [shareOpen, setShareOpen] = React.useState(false);
  // Phase 17 — public reviews list for this dealer. Pulled lazily once
  // the dealer payload arrives so the storefront still renders fast.
  const [reviews, setReviews] = React.useState<SellerReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get<StorefrontPayload>(`/dealers/${id}`);
        setData(r.data);
      } catch (err) {
        setError(extractApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  React.useEffect(() => {
    if (!id) return;
    setReviewsLoading(true);
    api
      .get<SellerReview[]>(`/sellers/${id}/reviews?limit=10`)
      .then((r) => setReviews(r.data))
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="py-20">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        className="mx-auto max-w-xl px-4 py-20 text-center"
        data-testid="dealer-storefront-error"
      >
        <Store className="h-10 w-10 text-ink-muted mx-auto mb-3" aria-hidden />
        <h1 className="text-2xl font-black mb-2">Dealer not found</h1>
        <p className="text-ink-muted">{error ?? "This dealer page is unavailable."}</p>
        <Link
          to="/"
          className="inline-block mt-6 font-semibold text-brand underline"
        >
          Back to the marketplace
        </Link>
      </div>
    );
  }

  const { dealer, listings, stats } = data;
  const joined = new Date(stats.joined_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  });
  const storefrontUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/dealer/${dealer.id}`
      : `/dealer/${dealer.id}`;

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-8 md:py-12" data-testid="dealer-storefront">
      {/* ------- Hero ------- */}
      <section className="relative overflow-hidden rounded-3xl border border-surface-border bg-ink text-white mb-8">
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 bg-[radial-gradient(900px_400px_at_90%_-20%,#E11D2E_0%,transparent_60%)]"
        />
        <div className="relative p-6 md:p-10 flex items-start gap-5 flex-wrap">
          <Avatar name={dealer.username} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-white text-3xl md:text-4xl font-black tracking-tight">
                {dealer.full_name ?? dealer.username}
              </h1>
              <Badge variant="brand" size="sm">
                <BadgeCheck className="h-3.5 w-3.5 mr-1" aria-hidden />
                Active Dealer
              </Badge>
            </div>
            <p className="text-white/70 inline-flex items-center gap-3 text-sm flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Store className="h-4 w-4" aria-hidden /> @{dealer.username}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" aria-hidden /> Joined {joined}
              </span>
            </p>
            {/* Phase 31-D — public dealer contact details. Older /
                admin-granted dealers may not have set these yet, so we
                render nothing rather than blank fields. */}
            {(dealer.phone_number || dealer.contact_email) && (
              <p
                className="text-white/80 mt-1.5 inline-flex items-center gap-3 text-sm flex-wrap"
                data-testid="dealer-contact-info"
              >
                {dealer.phone_number && (
                  <span data-testid="dealer-contact-phone">📞 {dealer.phone_number}</span>
                )}
                {dealer.contact_email && (
                  <a
                    href={`mailto:${dealer.contact_email}`}
                    className="underline hover:text-white"
                    data-testid="dealer-contact-email"
                  >
                    ✉️ {dealer.contact_email}
                  </a>
                )}
              </p>
            )}
            {/* Phase 17 — rating chip below the byline. Light variant
                so it pops on the dark hero. */}
            <div className="mt-2">
              <StarRating
                value={dealer.average_rating ?? null}
                count={dealer.review_count ?? 0}
                size={16}
                className="text-white"
              />
            </div>
            {dealer.bio && (
              <p className="text-white/80 mt-3 max-w-2xl">{dealer.bio}</p>
            )}
            <div className="mt-4">
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => setShareOpen(true)}
                leadingIcon={<Share2 className="h-4 w-4" />}
                data-testid="dealer-share-btn"
              >
                Share storefront
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4 min-w-[220px]">
            <StatBlock
              label="Active"
              value={stats.active_listings}
              icon={<Boxes className="h-4 w-4" />}
              testid="dealer-stat-active"
            />
            <StatBlock
              label="Sold"
              value={stats.sold_count}
              testid="dealer-stat-sold"
            />
          </div>
        </div>
      </section>

      <ShareStorefrontModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        storefrontUrl={storefrontUrl}
        dealerName={dealer.full_name ?? dealer.username}
      />

      {/* ------- Reviews list (Phase 17) ------- */}
      <section className="mb-10" data-testid="dealer-reviews-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold tracking-tight">Buyer reviews</h2>
          {dealer.review_count ? (
            <StarRating
              value={dealer.average_rating ?? null}
              count={dealer.review_count}
              size={14}
            />
          ) : null}
        </div>
        {reviewsLoading ? (
          <div className="py-8 text-center text-ink-muted text-sm">
            Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div className="py-10 text-center rounded-2xl border-2 border-dashed border-surface-border text-ink-muted">
            No reviews yet.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reviews.map((rev) => (
              <li
                key={rev.id}
                data-testid={`dealer-review-${rev.id}`}
                className="rounded-2xl border border-surface-border bg-white p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="font-semibold text-ink">
                    @{rev.reviewer_username}
                  </span>
                  <StarRating value={rev.rating} size={14} />
                </div>
                <div className="text-xs text-ink-muted mb-2">
                  {new Date(rev.created_at).toLocaleDateString()}
                </div>
                {rev.review_text && (
                  <p className="text-sm text-ink whitespace-pre-line">
                    {rev.review_text}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ------- Listings grid ------- */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold tracking-tight">Current inventory</h2>
          <span className="text-sm text-ink-muted">
            {listings.length} {listings.length === 1 ? "listing" : "listings"}
          </span>
        </div>

        {listings.length === 0 ? (
          <div
            data-testid="dealer-no-listings"
            className="py-16 text-center rounded-2xl border-2 border-dashed border-surface-border"
          >
            <p className="text-ink-muted">This dealer has no active listings right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {listings.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatBlock({
  label,
  value,
  icon,
  testid,
}: {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  testid?: string;
}) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-3"
    >
      <div className="text-[10px] uppercase tracking-wider text-white/70 inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-black font-display text-white">{value}</div>
    </div>
  );
}
